import { useSearchParams } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ErrorCard, Field, LoadingBlock, PageHeader, Pill } from "../components/jamora-admin/AdminBits";
import { AdminTable, FilterTabs, ListFrame, MoreFooter, SearchBox } from "../components/jamora-admin/ListBits";
import useAdminFetch from "../hooks/useAdminFetch";
import useAdminList from "../hooks/useAdminList";
import {
  CATALOGUE_CODE_TYPES, CATALOGUE_SOURCES, IPM_FORMS, MEDICINE_CATEGORIES, PAYER_SOURCES, PAYER_STATUS, PAYER_TYPES, formatCount, formatMoney,
} from "../lib/jamoraAdmin";
import { patchedSearch } from "../lib/searchParams";
import { formatDateTime } from "../lib/tickets";

const TABS = [["medicaments", "Médicaments"], ["organismes", "Organismes payeurs"]];
const ACTIVE_TABS = [["", "Tous"], ["true", "Actifs"], ["false", "Retirés"]];

const dash = (value) => (value === null || value === undefined || value === "" ? "—" : value);

// What each catalogue holds, source by source (counts are the whole catalogue, not the filtered list).
function SummaryLine({ summary, tab }) {
  if (!summary) return null;
  const parts = tab === "medicaments"
    ? summary.medicines.by_source.filter((s) => s.total > 0).map((s) => `${CATALOGUE_SOURCES[s.source] || s.source} : ${formatCount(s.total)}`)
    : summary.insurers.by_status.filter((s) => s.total > 0).map((s) => `${(PAYER_STATUS[s.status] || [s.status])[0]} : ${formatCount(s.total)}`);
  const total = tab === "medicaments" ? summary.medicines.total : summary.insurers.total;
  return (
    <p className="text-xs text-slate-500" data-testid="catalogue-summary">
      {formatCount(total)} entrée(s){parts.length > 0 && <> · {parts.join(" · ")}</>}
    </p>
  );
}

function MedicineDialog({ id, onClose }) {
  const { phase, data: row, error, reload } = useAdminFetch(id ? `/catalogue/medicines/${id}` : null, undefined, { enabled: !!id });
  return (
    <Dialog open={!!id} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-2xl" data-testid="medicine-dialog">
        <DialogHeader>
          <DialogTitle className="text-left">{row ? row.name : "Médicament du catalogue"}</DialogTitle>
          <DialogDescription className="text-left">{row ? `${CATALOGUE_SOURCES[row.source] || row.source} · code ${row.code}` : "Chargement…"}</DialogDescription>
        </DialogHeader>
        {phase === "error" && <ErrorCard message={error} onRetry={reload} testid="medicine-error" />}
        {(phase === "loading" || phase === "idle") && <LoadingBlock rows={3} />}
        {row && (
          <div className="space-y-4 text-sm">
            <dl className="divide-y divide-slate-100">
              <Field label="DCI">{dash(row.dci)}</Field>
              <Field label="Dosage">{dash(row.dosage)}</Field>
              <Field label="Catégorie">{MEDICINE_CATEGORIES[row.category] || row.category}</Field>
              <Field label="Classe ATC">{dash(row.atc)}</Field>
              <Field label="Fabricant">{dash(row.manufacturer)}</Field>
              <Field label="CIP7 / EAN13">{dash(row.cip7)} / {dash(row.ean13)}</Field>
              <Field label="Unités par boîte">{dash(row.units_per_box)}</Field>
              <Field label="Prix public indicatif">{formatMoney(row.public_price)}</Field>
              <Field label="Tarif fournisseur indicatif">{formatMoney(row.supplier_price)}</Field>
              <Field label="Particularités">
                <span className="inline-flex gap-1 flex-wrap">
                  {row.rx && <Pill tone="amber">Sur ordonnance</Pill>}{row.narcotic && <Pill tone="red">Stupéfiant</Pill>}{row.sensitive && <Pill tone="violet">Sensible</Pill>}
                  {!row.rx && !row.narcotic && !row.sensitive && "Aucune"}
                </span>
              </Field>
              <Field label="État">{row.active ? <Pill tone="emerald">Actif</Pill> : <Pill>Retiré</Pill>}{row.contributed && <span className="ml-1"><Pill tone="sky">Contribution d'une pharmacie</Pill></span>}</Field>
              <Field label="Mis à jour">{row.updated_at ? formatDateTime(row.updated_at) : "—"}</Field>
            </dl>
            <section>
              <h3 className="text-sm font-semibold text-slate-900 mb-1">Autres codes</h3>
              {row.codes.length === 0 ? <p className="text-slate-500" data-testid="medicine-no-codes">Aucun autre code connu.</p> : (
                <ul className="divide-y divide-slate-100" data-testid="medicine-codes">
                  {row.codes.map((c) => (
                    <li key={`${c.type}-${c.code}`} className="flex items-baseline justify-between gap-3 py-1">
                      <span><span className="font-medium text-slate-900">{c.code}</span> <span className="text-slate-500">{CATALOGUE_CODE_TYPES[c.type] || c.type}</span></span>
                      <span className="text-slate-600">{c.purchase_price === null ? "" : `achat ${formatMoney(c.purchase_price)}`}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MedicinesList({ params, update, search }) {
  const source = params.get("source") || "";
  const active = params.get("active") || "";
  const list = useAdminList("/catalogue/medicines", { source, active, search });
  const columns = [
    { id: "name", label: "Médicament", primary: true, cell: (m) => <span>{m.name}<span className="block text-xs text-slate-500">{[m.dci, m.dosage].filter(Boolean).join(" · ")}</span></span> },
    { id: "source", label: "Source", cell: (m) => CATALOGUE_SOURCES[m.source] || m.source },
    { id: "code", label: "Code", hide: "lg", cell: (m) => m.code },
    { id: "category", label: "Catégorie", hide: "lg", cell: (m) => MEDICINE_CATEGORIES[m.category] || m.category },
    { id: "public", label: "Prix public", cell: (m) => formatMoney(m.public_price) },
    { id: "supplier", label: "Tarif fournisseur", hide: "lg", cell: (m) => formatMoney(m.supplier_price) },
    { id: "flags", label: "État", cell: (m) => (
      <span className="inline-flex gap-1 flex-wrap">{!m.active && <Pill>Retiré</Pill>}{m.rx && <Pill tone="amber">Ordonnance</Pill>}{m.narcotic && <Pill tone="red">Stupéfiant</Pill>}{m.active && !m.rx && !m.narcotic && "—"}</span>
    ) },
  ];
  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <select aria-label="Source" value={source} onChange={(e) => update({ source: e.target.value })} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="catalogue-source">
          <option value="">Toutes les sources</option>
          {Object.entries(CATALOGUE_SOURCES).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
        </select>
        <FilterTabs label="État" tabs={ACTIVE_TABS} value={active} onChange={(v) => update({ active: v })} testid="catalogue-active" />
      </div>
      <ListFrame list={list} empty={{ icon: BookOpen, title: "Aucun médicament", hint: source || active || search ? "Essayez un autre filtre." : "Le catalogue est vide.", testid: "catalogue-empty" }}>
        <AdminTable columns={columns} rows={list.items} rowKey={(m) => m.id} testid="catalogue-table" onRowClick={(m) => update({ fiche: String(m.id) })} />
        <MoreFooter list={list} noun="médicament(s)" testid="catalogue" />
      </ListFrame>
    </>
  );
}

function InsurersList({ params, update, search }) {
  const status = params.get("status") || "";
  const type = params.get("type") || "";
  const list = useAdminList("/catalogue/insurers", { status, type, search });
  const columns = [
    { id: "name", label: "Organisme", primary: true, cell: (a) => (
      <span>{a.name}{a.merged_into && <span className="block text-xs text-slate-500">Doublon fusionné dans « {a.merged_into.name} »</span>}</span>
    ) },
    { id: "type", label: "Type", cell: (a) => `${PAYER_TYPES[a.type] || a.type}${a.ipm_form ? ` · ${IPM_FORMS[a.ipm_form] || a.ipm_form}` : ""}` },
    { id: "status", label: "Statut", cell: (a) => { const [text, tone] = PAYER_STATUS[a.status] || [a.status, "slate"]; return <Pill tone={tone}>{text}</Pill>; } },
    { id: "source", label: "Origine", hide: "lg", cell: (a) => PAYER_SOURCES[a.source] || a.source },
    { id: "code", label: "Code", hide: "lg", cell: (a) => a.code },
    { id: "contact", label: "Contact", hide: "lg", cell: (a) => [a.phone, a.email].filter(Boolean).join(" · ") || "—" },
    { id: "active", label: "Actif", cell: (a) => (a.active ? "Oui" : <Pill>Non</Pill>) },
  ];
  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <select aria-label="Statut" value={status} onChange={(e) => update({ status: e.target.value })} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="payer-status">
          <option value="">Tous les statuts</option>
          {Object.entries(PAYER_STATUS).map(([code, [label]]) => <option key={code} value={code}>{label}</option>)}
        </select>
        <select aria-label="Type d'organisme" value={type} onChange={(e) => update({ type: e.target.value })} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="payer-type">
          <option value="">Tous les types</option>
          {Object.entries(PAYER_TYPES).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
        </select>
      </div>
      <ListFrame list={list} empty={{ icon: BookOpen, title: "Aucun organisme", hint: status || type || search ? "Essayez un autre filtre." : "Le catalogue des organismes payeurs est vide.", testid: "payer-empty" }}>
        <AdminTable columns={columns} rows={list.items} rowKey={(a) => a.id} testid="payer-table" />
        <MoreFooter list={list} noun="organisme(s)" testid="payer" />
      </ListFrame>
    </>
  );
}

// The catalogues every pharmacy draws from (read only): the multi-source medicine catalogue and the payers' catalogue.
export default function JamoraAdminCatalogues() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "organismes" ? "organismes" : "medicaments";
  const search = params.get("q") || "";
  const fiche = Number(params.get("fiche")) || null;
  const { data: summary } = useAdminFetch("/catalogue/summary");
  const update = (patch) => setParams(patchedSearch(patch), { replace: true });
  const changeTab = (next) => update({ tab: next === "medicaments" ? "" : next, source: "", active: "", status: "", type: "", fiche: "" });

  return (
    <div className="space-y-4" data-testid="admin-catalogues">
      <PageHeader title="Catalogues" description="Les catalogues publics dont toutes les pharmacies s'inspirent : médicaments de chaque source et organismes payeurs. Lecture seule." />
      <FilterTabs label="Catalogue" tabs={TABS} value={tab} onChange={changeTab} testid="catalogue-tab" />
      <SummaryLine summary={summary} tab={tab} />
      <SearchBox value={search} onChange={(v) => update({ q: v })} placeholder={tab === "medicaments" ? "Nom, code, DCI, fabricant, CIP…" : "Nom ou code de l'organisme…"} testid="catalogue-search" />
      {tab === "medicaments" ? <MedicinesList params={params} update={update} search={search} /> : <InsurersList params={params} update={update} search={search} />}
      <MedicineDialog id={tab === "medicaments" ? fiche : null} onClose={() => update({ fiche: "" })} />
    </div>
  );
}
